<template>
  <div
    class="p-4 md:p-8 bg-white dark:bg-bs-dark-bg-over rounded-3xl text-left mb-4 md:mb-0 2xl:flex-grow flex flex-col relative"
  >
    <div class="flex justify-between flex-row">
      <h4 class="text-bs-gray-dark dark:text-white font-bold text-2xl w-8/12">
        Equity Performance
      </h4>
      <div class="flex items-center">
        <div class="flex items-center mr-1">
          <button v-for="(t,index) in timeframes" :key="index" @click="onChangeTimeframe(t)" 
            class="border-bs-blue-dark text-xs border px-2 py-1"
            :class="[
              t.value === timeframe ? 'bg-bs-blue-dark text-white' : 'text-bs-blue-dark ',
              index === 0 ? 'rounded-l-md' : '',
              index === 2 ? 'rounded-r-md' : ''
            ]" >
            
            {{t.title}}
          </button>
        </div>
        <router-link to="/settings">
          <img src="/img/menu/settings.selected.svg" alt="Go to settings" />
        </router-link>
      </div>
    </div>
    <template v-if="data && data.length">
      <p class="text-bs-gray dark:text-bs-gray mb-8 w-8/12">
        From {{ formatDate(startDate) }}, with
        {{ startingEquity && formatCurrency(startingEquity) }} as the initial equity, and
        utilising {{ true ? `${stake}% equity` : `${formatCurrency(stake)} fixed stake` }},
        the result is:
      </p>
      <apexchart
        type="line"
        :height="height"
        :options="trendOptions"
        :series="trendData"
        class="flex-grow"
        v-if="showChart"
      ></apexchart>
    </template>
    <div v-else class="flex-grow flex items-center justify-center">
      <div class="text-center text-bs-gray dark:text-bs-gray">
        <p class="text-lg" v-if="!startingEquity">
          Set your
          <router-link to="/settings" class="text-bs-blue-dark underline">Starting Equity</router-link>
          to get started.
        </p>
        <p class="text-lg" v-else>No tips in this period.</p>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { format, isAfter } from "date-fns";
import Linear from "d3-regression/src/linear";
import subDays from "date-fns/subDays/index";

const regression = Linear()
  .x((d) => d.x)
  .y((d) => d.y);

export default {
  data: function () {
    return {
      html: "",
      height: 400,
      showChart: true,
      timeframes: [
        {
          title: "Previous Month",
          value: 'PREVMONTH'
        },
        {
          title: "This Month",
          value: 'THISMONTH'
        },
        {
          title: "Last 10 days",
          value: '10DAYS'
        },
      ]
    };
  },
  watch: {
    tips: {
      deep:true,
      handler: function() {
        this.showChart = false;
        this.$nextTick(() =>{
          this.showChart = true
        })
      }
    },
    accaHistory: {
      deep: true,
      handler: function() {
        this.showChart = false;
        this.$nextTick(() => { this.showChart = true });
      }
    }
  },
  computed: {
    ...mapGetters({
      tips: "tips",
      settings: "settings",
      tipsByDates: "tipsByDate",

    }),
    ...mapState({
      timeframe: 'timeframe',
      accaHistory: (state) => state.accumulators.history,
    }),
    currency: function () {
      return this?.settings?.currencyCode || "EUR";
    },
    startingEquity: function () {
      return this?.settings?.startingEquity;
    },
    tippingStyle: function () {
      return this?.settings?.tippingStyle;
    },
    stake: function () {
      return this?.settings?.stakeAmount;
    },
    setupDate: function () {
      return this.settings?.setupDate ? new Date(this.settings.setupDate) : undefined;
    },
    
    data: function () {
      // Tips: compute equity from outcomes instead of trusting the stored
      // virtualEquityAfterTip (which is set at tip-creation and never updated
      // on settlement, so wins are under-reported by stake*odds).
      const settlementDelta = (status, stake, odds) => {
        if (!stake || !odds) return 0;
        switch ((status || "").toUpperCase()) {
          case "SUCCESS":
          case "WIN":      return stake * (odds - 1);
          case "FAIL":
          case "LOSS":     return -stake;
          case "VOID":     return 0;
          case "HALFWIN":  return (stake / 2) * (odds - 1);
          case "HALFLOSS": return -stake / 2;
          default:         return 0;
        }
      };

      const tipPoints = this.tips
        ?.filter(item => item.status !== 'PENDING')
        ?.map(tip => ({
          type: 'tip',
          x: new Date(tip.createdDate),
          stake: parseFloat(tip.virtualTipAmount) || 0,
          odds: parseFloat(tip.oddValue) || 0,
          status: tip.status,
          title: `${tip.homeTeam} v ${tip.awayTeam}`,
        })) || [];

      const accaPoints = (this.accaHistory || [])
        .filter(a => (a.status === 1 || a.status === 2) && a.settledDate)
        .map(a => ({
          type: 'acca',
          x: new Date(a.settledDate),
          status: a.status,
          odds: Number(a.effectiveOdds != null ? a.effectiveOdds : a.totalOdds),
          title: `Daily Acca · ${a.legCount} legs`,
        }));

      const merged = [...tipPoints, ...accaPoints]
        .sort((a, b) => a.x.getTime() - b.x.getTime());

      // Walk events chronologically. Apply each tip's settlement delta + each
      // acca's stake×odds outcome to a running equity starting from settings.
      const stakePct = (this.stake || 0) / 100;
      let lastEquity = this.startingEquity || 0;

      return merged.map(e => {
        if (e.type === 'tip') {
          lastEquity += settlementDelta(e.status, e.stake, e.odds);
        } else {
          const stake = lastEquity * stakePct;
          const result = e.status === 1 && e.odds > 0
            ? stake * (e.odds - 1)
            : -stake;
          lastEquity += result;
        }
        return { x: e.x, y: lastEquity, _title: e.title, _type: e.type };
      });
    },

    startDate: function () {
      return this.data?.[0]?.x
    },
    finalEquity: function () {
      return this.data?.[this.data.length - 1]?.y
    },

    trendData: function () {
      if (!this.data?.length) return [];
      let regressed = regression(this.data);
      let numberOfDays = Object.keys(this.tipsByDates).sort((a, b) => (a > b ? 1 : -1));
      let time = new Date(numberOfDays[0]);
      let length = numberOfDays.length + 5;

      let perfomanceReg = [];
      let equity = [];
      for (var x = 1; x < length; x++) {
        let t = time;

        perfomanceReg.push({
          x: time,
          y: regressed.predict(t),
        });
        equity.push({
          x: time,
          y: this.startingEquity,
        });

        t = t.getTime();
        t = t + 1000 * 60 * 60 * 24;

        time = new Date(t);
      }
      
      return [
        {
          name: "Tips",
          type: "line",
          data: this.data,
        },

        {
          name: "Trend",
          type: "line",
          data: perfomanceReg,
        },
      ];
    },

    trendOptions: function () {
      let trendData = this.trendData[1].data;
      let lastTrend = trendData[trendData.length - 1];
      trendData = this.trendData[0].data;
      let lastPoint = trendData[trendData.length - 1];

      return {
        legend: {
          show: false,
        },
        chart: {
          type: "line",
          foreColor: "#ccc",
          redrawOnParentResize: true,
        },
        colors: ["rgba(53,85,254,1)", "rgba(89,216,125,1)"],
        stroke: {
          curve: "smooth",
          width: window.innerWidth > 1920 ? 6 : 4,
          dashArray: [0, 10, 0],
          lineCap: "round",
        },
        fill: {
          type: "solid",
        },
        annotations: {
          yaxis: [
            {
              y: this.startingEquity,
              offsetY: 5,
              fillColor: "rgba(0,0,0,0.3)",
              strokeDashArray: 6,
              stroke: 4,
              label: {
                style: {
                  color: "#fff",
                  background: "rgba(0,0,0,0.3)",
                },
                text: `Starting Equity @ ${this.formatCurrency(this.startingEquity)}`,
              },
            },
          ],
          xaxis: isAfter(this.setupDate, new Date(this.startDate))
            ? [
                {
                  x: new Date(this.setupDate).getTime(),
                  strokeDashArray: 6,
                  stroke: 4,
                  borderColor: "rgba(229,104,12,1)",
                  label: {
                    borderColor: "rgba(229,104,12,1)",
                    offsetX: -3,
                    style: {
                      color: "#fff",
                      background: "rgba(229,104,12,1)",
                    },
                    text: "Change to starting equity",
                  },
                },
              ]
            : undefined,
          points: [
            {
              y: lastTrend.y,
              x: lastTrend.x.getTime(),
              // seriesIndex:1,
              marker: {
                size: 6,
                fillColor: "#fff",
                strokeColor: "rgba(89,216,125,1)",
                radius: 2,
              },
              label: {
                borderColor: "rgba(89,216,125,1)",
                offsetY: 0,
                offsetX: -30,
                style: {
                  color: "#fff",
                  background: "rgba(89,216,125,1)",
                },
                text: "Trendline",
              },
            },
            {
              y: lastPoint.y,
              x: lastPoint.x.getTime(),
              // seriesIndex:1,
              marker: {
                size: 6,
                fillColor: "#fff",
                strokeColor: "rgba(53,85,254,1)",
                radius: 2,
              },
              label: {
                borderColor: "rgba(53,85,254,1)",
                offsetY: 0,
                offsetX: -30,
                style: {
                  color: "#fff",
                  background: "rgba(53,85,254,1)",
                },
                text: this.formatCurrency(lastPoint.y),
              },
            },
          ],
        },
        yaxis: [
          {
            labels: {
              style: {
                fontSize: window.innerWidth > 1920 ? "1.1rem" : "0.8rem",
              },
              formatter: function (value) {
                return value.toFixed(0) + "€";
              },
            },
          },
        ],
        xaxis: {
          type: "datetime",
          tickAmount: window.innerWidth > 1920 ? 12 : 6,
          labels: {
            style: {
              fontSize: window.innerWidth > 1920 ? "1.1rem" : "0.8rem",
            },
            formatter: function (value) {
              if (value) {
                return format(new Date(value), "dd/MM"); // The formatter function overrides format property
              }
            },
          },
        },
        tooltip: {
          shared: true,

          style: {
            fontSize: window.innerWidth > 1920 ? "1.1rem" : "0.8rem",
          },
          y: {
            formatter: (y) => {
              if (typeof y !== "undefined") {
                return this.formatCurrency(y);
              }
              return y;
            },
            style: {
              fontSize: window.innerWidth > 1920 ? "1.1rem" : "0.8rem",
            },
          },
          x: {
            format: "DD/MM",
            formatter: (x,{seriesIndex, dataPointIndex}) => {
              if (seriesIndex !== 0) return format(x, "dd/MM");
              const point = this.data[dataPointIndex];
              const icon = point?._type === 'acca' ? '🎯 ' : '';
              return `${format(x, "dd/MM")}  - ${icon}${point?._title || ''}`;
            },
          },
        },
      };
    },
  },
  mounted: function () {
    window.addEventListener("resize", this.onResize);
    this.onResize();
  },
  unmounted: function () {
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize: function () {
      this.$nextTick(() => {
        let summaryContainer = document.querySelector(".summary-container");
        let topBar = document.querySelector(".top-bar");
        let windowWidth = window.innerWidth;
        if (windowWidth < 768) {
          this.height = windowWidth * 0.9;
        } else if (summaryContainer) {
          let { height: summaryHeight } = summaryContainer.getBoundingClientRect();
          let { height: topBarHeight } = topBar.getBoundingClientRect();
          let totalOthers = summaryHeight + topBarHeight + 210;
          let height = Math.floor(window.innerHeight - totalOthers);
          
          if (height !== this.height) {
            this.height = height;
          }
          // if (window.innerHeight > 900) {
          // 	this.height = window.innerHeight - this.$el.offsetTop - 250
          // } else {
          // 	this.height = 400;
          // }
        }
      });
    },
    calculateResult: function (status, odd, stake) {
      return status == "FAIL"
        ? -stake
        : status == "HALF_LOSS"
        ? -stake / 2
        : status == "VOID"
        ? 0
        : status == "HALF_WIN"
        ? (stake * (odd - 1)) / 2
        : stake * (odd - 1);
    },
    getStatus: function (status) {
      switch (status) {
        case 0:
        case "0":
        case "PENDING":
          return "PENDING";
        case 1:
        case "1":
        case "SUCCESS":
          return "SUCCESS";
        case 2:
        case "2":
        case "FAIL":
          return "FAIL";
        case 3:
        case "3":
        case "VOID":
          return "VOID";

        case 4:
        case "4":
        case "HALFWIN":
          return "HALF_WINN";
        case 5:
        case "5":
        case "HALFLOSS":
          return "HALF_LOSS";
      }
    },
    formatCurrency: function (n) {
      return (
        typeof n !== "undefined" &&
        n.toLocaleString("en-GB", {
          style: "currency",
          currency: this.currency,
          currencyDisplay: "symbol",
        })
      );
    },
    formatDate: function (str) {
      if (!str) {
        return "";
      }
      try {
        let d = new Date(str);
        if (d) {
          return format(d, "dd-MM-yyyy");
        }
      } catch (e) {
        console.log(e);
        console.log(str);
      }
    },
    onChangeTimeframe: function(val) {
      console.log(val)
      this.$store.commit('setTimeframe',val.value)
    }
  },
};
</script>

